import { useState, useEffect, useCallback, useRef } from 'react';
import { bffQuery, bffMutation } from '../lib/bffClient';
import { BFF_URL } from '../lib/runtimeEnv';
import { getToken, isTokenExpired, refreshAccessToken } from '../lib/auth';
import { getActiveClinicRef } from '../lib/clinicSignal';

const SESSION_QUERY = `
  query WhatsappSession {
    whatsappSession {
      clinicId state phone deviceLabel code codeExpiresAt linkedAt detail
    }
  }
`;

const UNLINK = `mutation UnlinkWhatsapp { unlinkWhatsapp }`;

/** The one state where a send can actually leave the building. */
export const canSendOnWhatsapp = (session) => session?.state === 'CONNECTED';

/** Where the BFF streams the pairing conversation. */
const PAIRING_PATH = '/integrations/whatsapp/pairing';

/**
 * The clinic's WhatsApp link.
 *
 * PAIRING IS A STREAM, NOT A POLL. WhatsApp expires each QR code after about twenty
 * seconds and issues the next; the pairing then completes on the gateway's own socket,
 * not in response to anything the browser does. Asking «what is the code now» every two
 * seconds either misses codes or asks far more often than it needs to, so the BFF
 * proxies the gateway's stream as text/event-stream and this hook reads it.
 *
 * READ WITH `fetch`, NOT `EventSource`. EventSource cannot set an Authorization header,
 * which would push the access token into a query string and therefore into access logs,
 * proxy logs and browser history. A fetch reader carries the Bearer token like every
 * other call and parses the same SSE frames.
 *
 * Outside pairing the link changes rarely and nothing polls: a settings screen left
 * open on a reception monitor should not ask the BFF a question every two seconds all
 * day.
 */
// `pollWhileDisconnected` is the ONLY polling left. Pairing used to be polled and is
// now streamed (see the header), so there is nothing to poll while a code is live —
// but DISCONNECTED still recovers silently in the background, and the compose dialog
// needs to see the channel become selectable without being closed and reopened.
export function useWhatsappSession({ pollWhileDisconnected = false } = {}) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The live pairing attempt: { kind, code, expiresAt, message } or null.
  const [pairing, setPairing] = useState(null);
  const controller = useRef(null);

  const refetch = useCallback(async () => {
    try {
      const data = await bffQuery(SESSION_QUERY);
      setSession(data?.whatsappSession || null);
      setError(null);
      return data?.whatsappSession || null;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  const stopPairing = useCallback(() => {
    // Aborting closes the HTTP response, which the BFF turns into a gRPC cancel. Without
    // it the gateway holds a fresh, half-linked device open for the full three-minute
    // window and the clinic's phone accumulates abandoned entries in «linked devices».
    controller.current?.abort();
    controller.current = null;
    setPairing(null);
  }, []);

  // Whatever happens — a tab closed, a route change, a component unmounted — the stream
  // must not outlive the screen that opened it.
  useEffect(() => () => { controller.current?.abort(); }, []);

  // DISCONNECTED is transient and recovers on its own (the service README says so),
  // which is why the compose dialog asks to watch it: an operator who fixes the link in
  // another tab, or simply waits, must see the channel become selectable without
  // closing and reopening the dialog. Opt-in, because a settings screen has no such
  // need and a poll nobody is watching is a request nobody wanted. PAIRING is NOT
  // polled here — the stream above delivers it.
  useEffect(() => {
    if (!pollWhileDisconnected || session?.state !== 'DISCONNECTED') return undefined;
    const timer = setInterval(refetch, 2500);
    return () => clearInterval(timer);
  }, [pollWhileDisconnected, session?.state, refetch]);

  const startLink = useCallback(async (method, phone) => {
    stopPairing();
    // A new attempt clears the last one's failure. Without this the screen keeps
    // reporting a gateway that was down two minutes ago while a code is on the way.
    setError(null);
    const token = await validToken();
    if (!token) throw new Error('Not authenticated');

    const url = new URL(PAIRING_PATH, BFF_URL);
    url.searchParams.set('method', method === 'PAIRING_CODE' ? 'PAIRING_CODE' : 'QR');
    if (phone) url.searchParams.set('phone', phone);

    const ctrl = new AbortController();
    controller.current = ctrl;

    // The clinic this pairing is FOR, from the `/c/:ref/…` route — the same header
    // bffClient puts on every GraphQL call. Without it the BFF inferred the doctor's
    // default clinic, so someone in two clinics linked a phone to clinic A while
    // clinic B's panel, which does send the header, went on reading «متصل نیست».
    const clinicRef = getActiveClinicRef();
    const res = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
        ...(clinicRef ? { 'x-active-clinic-id': clinicRef } : {}),
      },
      signal: ctrl.signal,
    });
    if (!res.ok || !res.body) {
      const body = await res.json().catch(() => null);
      controller.current = null;
      // The BFF's own sentence when it has one — it knows whether this was a missing
      // permission, a missing clinic or an unreachable gateway, and the screen does
      // not. An EMPTY message on purpose otherwise: copy belongs in the catalogue, so
      // the caller falls back to its own translated string rather than to a Persian
      // literal frozen into a hook.
      throw new Error(body?.error || '');
    }

    setPairing({ kind: 'CONNECTING', code: '', expiresAt: null, message: '' });
    // Deliberately not awaited: the stream lives for as long as the pairing does, and
    // the caller needs its button back the moment the first code can start arriving.
    readStream(res.body, ctrl, controller, { setPairing, refetch, setError });
    return true;
  }, [refetch, stopPairing]);

  const unlink = useCallback(async () => {
    stopPairing();
    await bffMutation(UNLINK);
    await refetch();
  }, [refetch, stopPairing]);

  return {
    session,
    loading,
    error,
    pairing,
    refetch,
    startLink,
    stopPairing,
    unlink,
    canSend: canSendOnWhatsapp(session),
  };
}

async function validToken() {
  let token = getToken();
  if (!token) return null;
  if (isTokenExpired(token)) {
    if (!(await refreshAccessToken())) return null;
    token = getToken();
  }
  return token;
}

/**
 * Reads SSE frames off the response body.
 *
 * Frames are separated by a blank line and a frame's `data:` is one JSON object. Lines
 * beginning `:` are the server's keep-alive comments and carry nothing — they exist so
 * an intermediary does not close a connection that is idle between two QR codes.
 */
async function readStream(body, ctrl, controller, { setPairing, refetch, setError }) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let split;
      while ((split = buffer.indexOf('\n\n')) >= 0) {
        const frame = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);
        const parsed = parseFrame(frame);
        if (!parsed) continue;

        if (parsed.event === 'error') {
          // Server copy, or nothing — see the comment on the failed-response branch.
          setError(new Error(parsed.data?.message || ''));
          setPairing(null);
          continue;
        }
        const { kind, code, expiresInSeconds, message } = parsed.data || {};
        if (kind === 'SUCCESS') {
          setPairing(null);
          await refetch();
          continue;
        }
        if (kind === 'TIMEOUT' || kind === 'ERROR') {
          setPairing({ kind, code: '', expiresAt: null, message: message || '' });
          continue;
        }
        setPairing({
          kind: kind || 'QR',
          code: code || '',
          // An absolute instant, not a countdown: a component that re-renders for its
          // own reasons must not restart the clock.
          expiresAt: expiresInSeconds > 0
            ? new Date(Date.now() + expiresInSeconds * 1000).toISOString()
            : null,
          message: message || '',
        });
      }
    }
  } catch (err) {
    // An abort is this hook's own doing — the operator navigated away — and is not a
    // failure to report.
    if (err?.name !== 'AbortError') setError(err);
  } finally {
    // The stream that just finished may not be the one now in flight: pressing «اتصال»
    // twice aborts the first, and its cleanup must not wipe the second one's code off
    // the screen.
    if (controller.current === ctrl) {
      controller.current = null;
      setPairing(null);
    }
  }
}

function parseFrame(frame) {
  let event = 'message';
  const data = [];
  for (const line of frame.split('\n')) {
    if (!line || line.startsWith(':')) continue;
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data.push(line.slice(5).trim());
  }
  if (!data.length) return null;
  try {
    return { event, data: JSON.parse(data.join('\n')) };
  } catch {
    return null;
  }
}

export default useWhatsappSession;
